import * as React from 'react'
import { type CalendarDay } from '@lib/validators/temporal.validator'
import { type TimeOfDay } from '@lib/validators/time-of-day.validator'
import { Link, useNavigate } from 'react-router-dom'
import { ChevronLeft } from 'lucide-react'
import { useAuth } from '@/auth/AuthContext'
import { setsUpIntegrations } from '@/auth/access'
import { isInVenueSupportMode } from '@/auth/roles'
import { useVenue, useVenues } from '@/hooks/useVenues'
import { useTrackAvailability } from '@/hooks/useEvents'
import type { TrackAvailabilityQuery } from '@lib/input/track-availability.input'
import { useCreateEvent } from '@/hooks/useEvents'
import { useOrganization } from '@/hooks/useOrganization'
import { useGhlAvailability, useGhlNameFormat } from '@/hooks/useGoHighLevel'
import { ghlKeys } from '@/hooks/queryKeys'
import { useQueryClient } from '@tanstack/react-query'
import { useToast } from '@/components/ui/toast'
import { Callout } from '@/components/ui/callout'
import { GhlLeadPicker } from '@/components/gohighlevel/GhlLeadPicker'
import { ghlLeadsService } from '@/services/GhlLeadsService'
import { eventService } from '@/services/EventService'
import { renderEventName } from '@lib/helpers/event-name-format'
import { GHL_MAX_LINKS_PER_EVENT } from '@lib/constants/gohighlevel'
import { PREFILL_READS, fillPrefillGaps, prefillHasGaps } from '@lib/helpers/ghl-prefill'
import type { GhlCoupleDto, GhlPrefillDto } from '@lib/dto/gohighlevel.dto'
import { requestErrorMessage } from '@/helpers/apiClient'
import { Button } from '@/components/ui/button'
import { FormActions } from '@/components/ui/form-actions'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'
import { Field } from '@/components/ui/field'
import { TimePicker } from '@/components/ui/time-picker'
import { DatePicker } from '@/components/ui/date-picker'
import { useBookedDays } from '@/hooks/useBookedDays'
import { HELD_LABEL, useHeldDays } from '@/hooks/useHeldDays'
import { bookedLabel, todayIso } from '@/lib/bookedDays'
import { Select } from '@/components/ui/select'
import { TrackPicker } from '@/components/TrackPicker'
import { PageContainer } from '@/components/ui/page-container'
import { PageHeader } from '@/components/ui/page-header'
import { FormError } from '@/components/ui/form-error'
import { toIsoTime } from '@/lib/eventTime'
import { createEventInputSchema, EVENT_NAME_MAX, EVENT_NOTES_MAX, MAX_EVENT_DAY, MIN_EVENT_DAY } from '@lib/input/event.input'

/**
 * The time a picker shows: what somebody chose, or the venue's usual one once
 * there is a date for it to be on.
 *
 * Undated events have no times at all — that is the API's own all-or-nothing
 * rule — so nothing is offered until a date exists.
 */
export function shownTime(
  picked: TimeOfDay | '',
  dated: boolean,
  venueDefault: TimeOfDay | undefined,
): TimeOfDay | '' {
  if (picked !== '') return picked
  if (!dated) return ''
  return venueDefault ?? ''
}

/** A client taken from a picked lead, added once the event exists. */
interface ClientFromGhl {
  name: string
  email: string
  phone: string | null
}

/** Create an event on its own page: pick a venue (and track), name it, set the
 * day and its time window, and a guest count. On success, opens the event's page.
 * Inside a venue the venue is already settled, so only the track is asked for. */
export function CreateEventPage() {
  const { user } = useAuth()
  const venuesQuery = useVenues()
  const createEvent = useCreateEvent()
  const navigate = useNavigate()

  // Inside a venue there is nothing to choose: the event belongs to that venue.
  const focusVenueId = isInVenueSupportMode(user) ? user.supportVenue.venueId : null

  const [name, setName] = React.useState('')
  const [venueId, setVenueId] = React.useState(focusVenueId ?? '')
  // The lanes somebody picked. Several are allowed: an event may take the whole
  // venue. Empty means nothing picked yet, which for a single-lane venue is
  // resolved below rather than asked about.
  const [pickedTrackIds, setPickedTrackIds] = React.useState<string[]>([])
  const [date, setDate] = React.useState<CalendarDay | ''>('')
  // What the person actually picked; `''` until they do. The times *shown* are
  // derived from these and the venue — see below.
  const [startTimeInput, setStartTime] = React.useState<TimeOfDay | ''>('')
  const [endTimeInput, setEndTime] = React.useState<TimeOfDay | ''>('')
  const [guests, setGuests] = React.useState('')
  const [notes, setNotes] = React.useState('')
  const [error, setError] = React.useState<string | null>(null)
  // A problem with one field shows under that field, not at the foot of the form.
  const [fieldError, setFieldError] = React.useState<{ field: 'name' | 'guestCount'; message: string } | null>(null)
  // The GoHighLevel lead this event is for, and what it filled in. Optional.
  const [ghlPick, setGhlPick] = React.useState<{ couple: GhlCoupleDto; prefill: GhlPrefillDto | null } | null>(null)
  // A typed name is the person's; a picked couple only names the event until then.
  const [nameEdited, setNameEdited] = React.useState(false)
  const [clientFromGhl, setClientFromGhl] = React.useState<ClientFromGhl | null>(null)
  const [ghlProblem, setGhlProblem] = React.useState<string | null>(null)
  // Reading a lead takes a moment. Each pick is numbered, so a slower answer
  // for an earlier pick (or one since cleared) is dropped rather than filling
  // in the wrong couple; and what the person did meanwhile is read from here,
  // not from the render the pick started in.
  const pickSeq = React.useRef(0)
  const latest = React.useRef({ date, nameEdited })
  React.useEffect(() => {
    latest.current = { date, nameEdited }
  }, [date, nameEdited])
  const { toast } = useToast()
  const queryClient = useQueryClient()
  const hasGhl = useOrganization().data?.features.includes('gohighlevel') ?? false
  const ghlAvailability = useGhlAvailability(venueId, hasGhl)
  const ghlConnected = hasGhl && ghlAvailability.data?.connected === true
  const nameFormat = useGhlNameFormat(venueId, ghlConnected).data?.nameFormat

  const venues = venuesQuery.data?.venues ?? []
  const listed = venues.find((v) => v.id === venueId) ?? null
  // The list leaves every space's `photos` empty — signing a URL per photograph
  // per venue would be most of the cost of the page, for pictures a list does
  // not show. The track cards do show them, so the chosen venue is read again in
  // full. Until it arrives the listed one drives the form, so nothing waits:
  // the cards appear immediately with the drawing and take their photographs a
  // moment later.
  const detailQuery = useVenue(venueId)
  const venue = detailQuery.data ?? listed
  // **Every** concurrent venue needs a track, not only one with several. The
  // API refuses a trackless booking at any of them ("This venue runs concurrent
  // events; choose a track"), so hiding the field when there was exactly one
  // track made that venue impossible to book from this form at all — the
  // request was rejected and the form had nothing to point at.
  const needsTrack = venue?.supportsConcurrentEvents === true
  const showTracks = needsTrack && venue !== null && venue.tracks.length > 0
  // One track is not a choice, so it is taken rather than asked for — derived
  // rather than set, because the venue arrives two different ways (chosen here,
  // or settled from the session) and only one of them runs a handler.
  const onlyTrack = needsTrack && venue !== null && venue.tracks.length === 1 ? venue.tracks[0] : undefined
  const trackIds = pickedTrackIds.length > 0 ? pickedTrackIds : onlyTrack ? [onlyTrack.id] : []
  // The month the date picker is showing, for its booked-day marks; until it
  // is turned, the month of the chosen date, or this one.
  const [calendarMonth, setCalendarMonth] = React.useState<CalendarDay | null>(null)
  const booked = useBookedDays({ venueId, trackIds, month: calendarMonth ?? (date || todayIso()) })
  // Days another couple's inquiry holds: a warning before booking, never a refusal.
  const held = useHeldDays({ venueId, trackIds, month: calendarMonth ?? (date || todayIso()) })

  // **The venue's usual evening, shown rather than applied behind the form.**
  // Dating an event gives it that window — the API fills it in when the times
  // are left out — but the pickers stayed on "Set time", so the form said
  // nothing about the 17:00–23:00 the event was actually going to get. Now the
  // times appear the moment there is a date to hang them on, and changing one
  // is an edit of something visible instead of a discovery afterwards.
  //
  // Derived, not stored: the venue can change after the date is set, and an
  // untouched window should follow it. `''` means nobody has picked, which is
  // unambiguous because `TimePicker` only ever emits a real time.
  const startTime = shownTime(startTimeInput, date !== '', venue?.defaultStartsAt)
  const endTime = shownTime(endTimeInput, date !== '', venue?.defaultEndsAt)
  const usingVenueWindow = date !== '' && startTimeInput === '' && endTimeInput === ''
  // One window, used by the availability question and by the write. Two
  // derivations of "what is this event's window" is how a form comes to ask
  // about one thing and book another.
  const day = date === '' ? null : date
  const startsAtIso = toIsoTime(day, startTime)
  const endsAtIso = toIsoTime(day, endTime)
  // An end earlier than the start is the next morning (the API rolls it a day,
  // see `eventWindow`), which is right for a reception that runs past midnight
  // and a surprise for anyone who meant the afternoon. So the form says it.
  const endsNextDay = startTime !== '' && endTime !== '' && endTime < startTime

  function chooseVenue(id: string): void {
    setVenueId(id)
    setPickedTrackIds([]) // track choices belong to the venue
    // A lead is the venue's: another venue's connections don't hold it.
    pickSeq.current += 1
    setGhlPick(null)
    setClientFromGhl(null)
  }

  /** The event's name for a picked couple, in the company's format, on `day`. */
  function nameFor(prefill: GhlPrefillDto, day: CalendarDay | ''): string | null {
    if (nameFormat === undefined) return null
    const named = renderEventName(nameFormat, prefill, day === '' ? null : day)
    return named === '' ? null : named
  }

  function chooseDate(day: CalendarDay | ''): void {
    setDate(day)
    // A date in the format follows the date, until the person names the event themselves.
    if (ghlPick?.prefill && !nameEdited) {
      const named = nameFor(ghlPick.prefill, day)
      if (named !== null) setName(named)
    }
  }

  async function pickLead(couple: GhlCoupleDto): Promise<void> {
    pickSeq.current += 1
    const seq = pickSeq.current
    const isCurrent = (): boolean => seq === pickSeq.current
    setGhlProblem(null)
    setNameEdited(false)
    latest.current.nameEdited = false
    setGhlPick({ couple, prefill: null })
    let prefill: GhlPrefillDto | null = null
    try {
      // The newest opportunity first; up to two more fill what it lacks.
      for (const opportunity of couple.opportunities.slice(0, PREFILL_READS)) {
        if (prefill !== null && !prefillHasGaps(prefill)) break
        const read = await ghlLeadsService.prefill(venueId, {
          connectionId: opportunity.connectionId,
          opportunityId: opportunity.opportunityId,
        })
        if (!isCurrent()) return
        prefill = prefill === null ? read : fillPrefillGaps(prefill, read)
      }
    } catch {
      if (!isCurrent()) return
      if (prefill === null) {
        // The link still works without them.
        setGhlProblem('Couldn’t read this lead’s details from GoHighLevel; fill them in by hand.')
        return
      }
    }
    if (prefill === null) return
    const read = prefill
    setGhlPick({ couple, prefill: read })
    if (!latest.current.nameEdited) {
      const named = nameFor(read, latest.current.date)
      if (named !== null) setName(named)
    }
    const guestCount = read.guestCount
    if (guestCount !== null) setGuests((typed) => (typed.trim() === '' ? String(guestCount) : typed))
    const leadNotes = read.notes
    if (leadNotes !== null) setNotes((typed) => (typed.trim() === '' ? leadNotes : typed))
    if (read.clientEmail !== null) {
      setClientFromGhl({ name: read.clientName ?? read.clientEmail, email: read.clientEmail, phone: read.clientPhone })
    }
    const track = read.trackId
    if (venue?.supportsConcurrentEvents && track !== null && venue.tracks.some((t) => t.id === track)) {
      setPickedTrackIds((picked) => (picked.length === 0 ? [track] : picked))
    }
  }

  function clearLead(): void {
    // What it filled in stays: the person may have built on it.
    pickSeq.current += 1
    setGhlPick(null)
    setClientFromGhl(null)
    setGhlProblem(null)
  }

  // **Availability is asked for the window the form is actually showing.** Not
  // the raw inputs: an untouched window is the venue's usual evening, and that
  // is what the booking would take — so asking about anything else would answer
  // a question nobody is going to ask. With no date there is nothing to ask, and
  // every lane comes back `unknown`.
  // Built as one shape or the other rather than by spreading optionals: the
  // query is a union, and "times need a date" is a rule the *shape* carries
  // now instead of a refine the compiler could not see.
  const availabilityAsk: TrackAvailabilityQuery =
    day === null
      ? { venueId }
      : {
          venueId,
          date: day,
          ...(startsAtIso !== null ? { startsAt: startsAtIso } : {}),
          ...(endsAtIso !== null ? { endsAt: endsAtIso } : {}),
        }
  const availabilityQuery = useTrackAvailability(availabilityAsk, showTracks)

  async function submit(e: React.FormEvent): Promise<void> {
    e.preventDefault()
    setError(null)
    setFieldError(null)
    const startsAt = startsAtIso
    const endsAt = endsAtIso
    const candidate = {
      name,
      venueId,
      // `trackIds`, not the retiring single-valued `eventTrackId` — the input
      // schema asks for the set, and sending both is refused. Still one id:
      // the API takes at most one until availability lands.
      ...(trackIds.length > 0 ? { trackIds } : {}),
      ...(date !== '' ? { date } : {}),
      ...(startsAt ? { startsAt } : {}),
      ...(endsAt ? { endsAt } : {}),
      ...(guests.trim() !== '' ? { guestCount: Number(guests) } : {}),
      ...(notes.trim() !== '' ? { notes } : {}),
      ...(ghlPick !== null
        ? {
            ghlLinks: ghlPick.couple.opportunities.slice(0, GHL_MAX_LINKS_PER_EVENT).map((o) => ({
              connectionId: o.connectionId,
              opportunityId: o.opportunityId,
              contactId: o.contactId,
              email: ghlPick.couple.email,
            })),
          }
        : {}),
    }
    const parsed = createEventInputSchema.safeParse(candidate)
    if (!parsed.success) {
      const issue = parsed.error.issues[0]
      const message = issue?.message ?? 'Check the form and try again.'
      const field = issue?.path[0]
      if (field === 'name' || field === 'guestCount') setFieldError({ field, message })
      else setError(message)
      return
    }
    try {
      const created = await createEvent.mutateAsync(parsed.data)
      if (clientFromGhl !== null) {
        try {
          await eventService.addClient(created.id, {
            name: clientFromGhl.name,
            email: clientFromGhl.email,
            ...(clientFromGhl.phone !== null ? { phone: clientFromGhl.phone } : {}),
          })
        } catch (err) {
          toast(`The event was created, but ${clientFromGhl.email} couldn’t be added as its client: ${requestErrorMessage(err)}`, {
            variant: 'warning',
          })
        }
      }
      // The couple has an event now, so the picker stops offering them.
      if (ghlPick !== null) void queryClient.invalidateQueries({ queryKey: ghlKeys.couples(venueId) })
      navigate(`/events/${created.id}`)
    } catch (err) {
      setError(requestErrorMessage(err))
    }
  }

  // The venue is settled from the session before its tracks are known, so wait
  // for it: submitting early would book a multi-track venue on whichever track
  // the API defaults to, without ever showing the Track field.
  // A missing name is said under the field when Create is pressed, rather than
  // the button sitting greyed out with no reason given.
  const canSubmit =
    venueId !== '' &&
    // A concurrent venue has to be told which lane, so the button waits for it
    // rather than sending a request the API is certain to refuse.
    (!needsTrack || trackIds.length > 0) &&
    !venuesQuery.isPending &&
    !createEvent.isPending

  return (
    <PageContainer className="space-y-6">
      <Link
        to="/events"
        className="inline-flex items-center gap-1 text-sm text-ink-muted transition-colors hover:text-ink"
      >
        <ChevronLeft className="h-4 w-4" /> Events
      </Link>

      <PageHeader
        title="New event"
        description="A plan is created for each space in the venue."
      />

      <form onSubmit={submit} className="space-y-5" noValidate>
        <Field label="Name" htmlFor="ev-name" error={fieldError?.field === 'name' ? fieldError.message : undefined}>
          <Input
            id="ev-name"
            value={name}
            maxLength={EVENT_NAME_MAX}
            onChange={(e) => {
              setName(e.target.value)
              setNameEdited(true)
              if (fieldError?.field === 'name') setFieldError(null)
            }}
            placeholder="e.g. Shylah + Nathan's Wedding"
            autoFocus
          />
        </Field>

        {focusVenueId === null ? (
          <Field label="Venue" htmlFor="ev-venue">
            <Select id="ev-venue" value={venueId} onChange={(e) => chooseVenue(e.target.value)}>
              <option value="">Select a venue…</option>
              {venues.map((v) => (
                <option key={v.id} value={v.id}>
                  {v.name}
                </option>
              ))}
            </Select>
          </Field>
        ) : null}

        {ghlConnected ? (
          <Field label="GoHighLevel lead (optional)">
            {ghlAvailability.data?.working === false ? (
              <Callout tone="attention">GoHighLevel isn’t working for this venue right now.</Callout>
            ) : (
              <GhlLeadPicker
                venueId={venueId}
                value={ghlPick?.couple ?? null}
                onPick={(couple) => void pickLead(couple)}
                onClear={clearLead}
                idPrefix="ev-ghl"
                canChooseStages={setsUpIntegrations(user)}
              />
            )}
            {ghlProblem ? <FormError className="mt-2">{ghlProblem}</FormError> : null}
          </Field>
        ) : null}

        {showTracks && venue ? (
          <Field
            label="Track"
            hint="This venue runs concurrent events — choose which part of it this one books."
          >
            <TrackPicker
              venue={venue}
              value={trackIds}
              onChange={setPickedTrackIds}
              availability={availabilityQuery.data?.tracks}
              loading={availabilityQuery.isFetching}
              failed={availabilityQuery.isError}
            />
          </Field>
        ) : null}

        {needsTrack && venue !== null && venue.tracks.length === 0 ? (
          // A concurrent venue with no tracks refuses every booking, and nothing
          // in this form could fix it. Say where it is fixed.
          <p className="text-sm text-danger-ink">
            {venue.name} runs concurrent events but has no tracks yet, so events cannot be booked
            there. Add one on the venue&rsquo;s page first.
          </p>
        ) : null}

        {/* A date makes the event a booking, and none an inquiry (the API's rule,
            \`event.repository.ts\`). The form had no word of it, so a couple's
            asked-about day entered here came out Booked. */}
        <Field
          label="Date (optional)"
          htmlFor="ev-date"
          hint="A date books the event. For an inquiry, leave it empty and add the dates they asked about once it’s created."
        >
          <DatePicker
            id="ev-date"
            value={date}
            onChange={chooseDate}
            min={MIN_EVENT_DAY}
            max={MAX_EVENT_DAY}
            marked={booked}
            markedLabel={bookedLabel(trackIds.length)}
            held={held}
            heldLabel={HELD_LABEL}
            onMonthChange={setCalendarMonth}
            className="w-full"
          />
        </Field>

        <div className="grid grid-cols-2 gap-3">
          <Field label="Start time">
            <TimePicker value={startTime} onChange={setStartTime} disabled={date === ''} ariaLabel="Event start time" triggerClassName="w-full" />
          </Field>
          <Field label="End time">
            <TimePicker value={endTime} onChange={setEndTime} disabled={date === ''} ariaLabel="Event end time" triggerClassName="w-full" />
          </Field>
        </div>
        {date === '' ? (
          <p className="text-xs text-ink-muted">Set a date to add start and end times.</p>
        ) : usingVenueWindow && venue !== null ? (
          <p className="text-xs text-ink-muted">
            Usual times at {venue.name}
            {endsNextDay ? ', ending the next day' : ''}. Change either time if this event runs differently.
          </p>
        ) : endsNextDay ? (
          <p className="text-xs text-ink-muted">Ends the next day.</p>
        ) : null}

        <Field label="Guest count (optional)" htmlFor="ev-guests" error={fieldError?.field === 'guestCount' ? fieldError.message : undefined}>
          <Input
            id="ev-guests"
            type="number"
            min={0}
            step={1}
            value={guests}
            onChange={(e) => {
              setGuests(e.target.value)
              if (fieldError?.field === 'guestCount') setFieldError(null)
            }}
            className="w-40"
          />
        </Field>

        {clientFromGhl !== null ? (
          <Field label="Client" hint="Added as the event’s client. They won’t be sent an invitation.">
            <div className="flex items-center gap-3">
              <span className="min-w-0 flex-1 truncate text-sm">
                {clientFromGhl.name} <span className="text-ink-muted">{clientFromGhl.email}</span>
              </span>
              <Button type="button" variant="ghost" size="sm" onClick={() => setClientFromGhl(null)}>
                Remove
              </Button>
            </div>
          </Field>
        ) : null}

        <Field label="Notes (optional)" htmlFor="ev-notes" hint="For venue staff only. Clients, planners and vendors never see these.">
          <Textarea id="ev-notes" rows={4} value={notes} maxLength={EVENT_NOTES_MAX} onChange={(e) => setNotes(e.target.value)} />
        </Field>

        {error ? <FormError>{error}</FormError> : null}

        <FormActions className="border-t border-line pt-5">
          <Button pending={createEvent.isPending} type="submit" disabled={!canSubmit}>
            {createEvent.isPending ? 'Creating…' : 'Create event'}
          </Button>
          <Button type="button" variant="ghost" asChild>
            <Link to="/events">Cancel</Link>
          </Button>
        </FormActions>
      </form>
    </PageContainer>
  )
}
